import {
  redirect,
  Outlet,
  RootRoute,
  Route,
  Router,
} from "@tanstack/react-router"

import { getDefaultThemeWrappedComponent } from "./DefaultThemeComponent"
import { getAuthProtectedComponent } from "./getAuthProtectedComponent"
import { getDeauthProtectedComponent } from "./getDeauthProtectedComponent"
import { OauthAuthorizeRootComponent } from "./OauthAuthorizeRootComponent"
import { RootComponent } from "./RootComponent"
import {
  About,
  AccountDetails,
  AccountIndex,
  AlertsIndex,
  DataConsent,
  DataSharingAcceptInvite,
  DataSharingFollowerPhoneCallAlerts,
  DataSharingFollowerTheme,
  DataSharingIndex,
  DataSharingInviteAccepted,
  DataSource,
  DayStats,
  Display,
  Following,
  ForgotPassword,
  Help,
  HelpAndFeedbackIndex,
  Home,
  IndexPage,
  InsulinAndMedication,
  InviteFollower,
  ManageFollower,
  ManageFollowing,
  ManageInvitation,
  Nightscout,
  Nightstand,
  NotFound,
  OauthAuthorizeForgotPassword,
  OauthAuthorizeIndexPage,
  OauthAuthorizeSignIn,
  OauthAuthorizeSignUp,
  OnboardingAcceptInvite,
  OnboardingDataConsent,
  OnboardingDataSource,
  OnboardingFollowerPhoneCallAlerts,
  OnboardingFollowerTheme,
  OnboardingGetStarted,
  OnboardingInviteAccepted,
  OnboardingNightscout,
  OnboardingPhoneCallAlerts,
  OnboardingRanges,
  OnboardingTheme,
  OnboardingUnitsOfMeasure,
  PhoneCallAlerts,
  PolicyViewer,
  PrivacyAndDataUse,
  Ranges,
  Reports,
  ResetPassword,
  SMSAlerts,
  SettingsIndex,
  Sharing,
  SignIn,
  SignUp,
  SourcesCreated,
  Theme,
} from "src/pages"

const rootRoute = new RootRoute({
  component: RootComponent,
  notFoundComponent: getDefaultThemeWrappedComponent(NotFound),
})

const oauthAuthorizeRootRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/oauth/authorize",
  component: OauthAuthorizeRootComponent,
})

const oauthAuthorizeIndexRoute = new Route({
  getParentRoute: () => oauthAuthorizeRootRoute,
  path: "/",
  component: OauthAuthorizeIndexPage,
})

const oauthAuthorizeSignInRoute = new Route({
  getParentRoute: () => oauthAuthorizeRootRoute,
  path: "/sign_in",
  component: OauthAuthorizeSignIn,
})

const oauthAuthorizeSignUpRoute = new Route({
  getParentRoute: () => oauthAuthorizeRootRoute,
  path: "/sign_up",
  component: OauthAuthorizeSignUp,
})

const oauthAuthorizeForgotPasswordRoute = new Route({
  getParentRoute: () => oauthAuthorizeRootRoute,
  path: "/forgot_password",
  component: OauthAuthorizeForgotPassword,
})

const publicRoute = new Route({
  getParentRoute: () => rootRoute,
  id: "public",
  component: getDeauthProtectedComponent(Outlet),
})

const indexRoute = new Route({
  getParentRoute: () => publicRoute,
  path: "/",
  component: IndexPage,
})

const signInRoute = new Route({
  getParentRoute: () => publicRoute,
  path: "/sign_in",
  component: SignIn,
})

const signUpRoute = new Route({
  getParentRoute: () => publicRoute,
  path: "/sign_up",
  component: SignUp,
})

const forgotPasswordRoute = new Route({
  getParentRoute: () => publicRoute,
  path: "/forgot_password",
  component: ForgotPassword,
})

const resetPasswordRoute = new Route({
  getParentRoute: () => publicRoute,
  path: "/users/$userId/password/edit",
  component: ResetPassword,
})

const policyViewerRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/policy-viewer",
  component: getDefaultThemeWrappedComponent(PolicyViewer),
  validateSearch: (
    search: Record<string, unknown>,
  ): {
    title: string;
    file: string;
  } => ({
    title: (search.title as string) ?? "",
    file: (search.file as string) ?? "",
  }),
})

const privateRoute = new Route({
  getParentRoute: () => rootRoute,
  id: "private",
  component: getAuthProtectedComponent(Outlet),
})

const nightstandRoute = new Route({
  getParentRoute: () => privateRoute,
  path: "/nightstand",
  component: Nightstand,
})

const nightstandFollowRoute = new Route({
  getParentRoute: () => privateRoute,
  path: "/nightstand/$accountId",
  component: Nightstand,
})

const homeRoute = new Route({
  getParentRoute: () => privateRoute,
  path: "/home",
  component: Home,
})

const homeFollowRoute = new Route({
  getParentRoute: () => privateRoute,
  path: "/home/$accountId",
  component: Home,
})

const accountRoute = new Route({
  getParentRoute: () => privateRoute,
  path: "/account",
})

const accountIndexRoute = new Route({
  getParentRoute: () => accountRoute,
  path: "/",
  component: AccountIndex,
})

const accountDataSourceRoute = new Route({
  getParentRoute: () => accountRoute,
  path: "/data_source",
})

const accountDataSourceIndexRoute = new Route({
  getParentRoute: () => accountDataSourceRoute,
  path: "/",
  component: DataSource,
})

const dataConsentRoute = new Route({
  getParentRoute: () => accountDataSourceRoute,
  path: "/data_consent/$sourceType",
  component: DataConsent,
})

const nightscoutRoute = new Route({
  getParentRoute: () => accountDataSourceRoute,
  path: "/nightscout",
  component: Nightscout,
})

const accountDetailsRoute = new Route({
  getParentRoute: () => accountRoute,
  path: "/details",
  component: AccountDetails,
})

const helpAndFeedbackRoute = new Route({
  getParentRoute: () => privateRoute,
  path: "/help_and_feedback",
})

const helpAndFeedbackIndexRoute = new Route({
  getParentRoute: () => helpAndFeedbackRoute,
  path: "/",
  component: HelpAndFeedbackIndex,
})

const helpAndFeedbackHelpRoute = new Route({
  getParentRoute: () => helpAndFeedbackRoute,
  path: "/help",
  component: Help,
})

const helpAndFeedbackAboutRoute = new Route({
  getParentRoute: () => helpAndFeedbackRoute,
  path: "/about",
  component: About,
})

const helpAndFeedbackPrivacyRoute = new Route({
  getParentRoute: () => helpAndFeedbackRoute,
  path: "/privacy",
  component: PrivacyAndDataUse,
})

const onboardingUnitsOfMeasureRoute = new Route({
  getParentRoute: () => privateRoute,
  path: "/onboarding/units_of_measure",
  component: OnboardingUnitsOfMeasure,
})

const onboardingGetStartedRoute = new Route({
  getParentRoute: () => privateRoute,
  path: "/onboarding/get_started",
  component: OnboardingGetStarted,
})

const onboardingDataSourceRoute = new Route({
  getParentRoute: () => privateRoute,
  path: "/onboarding/data_source",
})

const onboardingDataSourceIndexRoute = new Route({
  getParentRoute: () => onboardingDataSourceRoute,
  path: "/",
  component: OnboardingDataSource,
})

const onboardingNightscoutRoute = new Route({
  getParentRoute: () => onboardingDataSourceRoute,
  path: "/nightscout",
  component: OnboardingNightscout,
})

const onboardingDataConsentRoute = new Route({
  getParentRoute: () => onboardingDataSourceRoute,
  path: "/data_consent/$sourceType",
  component: OnboardingDataConsent,
})

const onboardingRangesRoute = new Route({
  getParentRoute: () => privateRoute,
  path: "/onboarding/ranges",
  component: OnboardingRanges,
})

const onboardingPhoneCallAlertsRoute = new Route({
  getParentRoute: () => privateRoute,
  path: "/onboarding/phone_call_alerts",
  component: OnboardingPhoneCallAlerts,
})

const onboardingThemeRoute = new Route({
  getParentRoute: () => privateRoute,
  path: "/onboarding/theme",
  component: OnboardingTheme,
})

const onboardingAcceptInviteRoute = new Route({
  getParentRoute: () => privateRoute,
  path: "/onboarding/accept_invite",
  component: Outlet,
})

const onboardingAcceptInviteIndexRoute = new Route({
  getParentRoute: () => onboardingAcceptInviteRoute,
  path: "/",
  component: OnboardingAcceptInvite,
})

const onboardingAcceptInviteSuccessRoute = new Route({
  getParentRoute: () => onboardingAcceptInviteRoute,
  path: "/$accountId/success",
  component: OnboardingInviteAccepted,
})

const onboardingAcceptInvitePhoneCallAlertsRoute = new Route({
  getParentRoute: () => onboardingAcceptInviteRoute,
  path: "/$accountId/phone_call_alerts",
  component: OnboardingFollowerPhoneCallAlerts,
})

const onboardingAcceptInviteThemeRoute = new Route({
  getParentRoute: () => onboardingAcceptInviteRoute,
  path: "/$accountId/theme",
  component: OnboardingFollowerTheme,
})

const settingsRoute = new Route({
  getParentRoute: () => privateRoute,
  path: "/settings",
})

const settingsIndexRoute = new Route({
  getParentRoute: () => settingsRoute,
  path: "/",
  component: SettingsIndex,
})

const settingsIndexFollowRoute = new Route({
  getParentRoute: () => settingsRoute,
  path: "/$accountId",
  component: SettingsIndex,
})

const alertsRoute = new Route({
  getParentRoute: () => settingsRoute,
  path: "/alerts",
})

const alertsIndexRoute = new Route({
  getParentRoute: () => alertsRoute,
  path: "/",
  component: AlertsIndex,
})

const alertsIndexFollowRoute = new Route({
  getParentRoute: () => alertsRoute,
  path: "/$accountId",
  component: AlertsIndex,
})

const alertsSMSRoute = new Route({
  getParentRoute: () => alertsRoute,
  path: "/sms",
  component: SMSAlerts,
})

const alertsSMSFollowRoute = new Route({
  getParentRoute: () => alertsRoute,
  path: "/sms/$accountId",
  component: SMSAlerts,
})

const alertsPhoneCall = new Route({
  getParentRoute: () => alertsRoute,
  path: "/phone_call",
  component: PhoneCallAlerts,
})

const alertsPhoneCallFollowRoute = new Route({
  getParentRoute: () => alertsRoute,
  path: "/phone_call/$accountId",
  component: PhoneCallAlerts,
})

const settingsRangesRoute = new Route({
  getParentRoute: () => settingsRoute,
  path: "/ranges",
  component: Ranges,
})

const settingsRangesFollowRoute = new Route({
  getParentRoute: () => settingsRoute,
  path: "/ranges/$accountId",
  component: Ranges,
})

const settingsThemeRoute = new Route({
  getParentRoute: () => settingsRoute,
  path: "/theme",
  component: Theme,
})

const settingsThemeFollowRoute = new Route({
  getParentRoute: () => settingsRoute,
  path: "/theme/$accountId",
  component: Theme,
})

const settingsDisplayRoute = new Route({
  getParentRoute: () => settingsRoute,
  path: "/display",
  component: Display,
})

const settingsDisplayFollowRoute = new Route({
  getParentRoute: () => settingsRoute,
  path: "/display/$accountId",
  component: Display,
})

const settingsInsulinAndMedicationRoute = new Route({
  getParentRoute: () => settingsRoute,
  path: "/insulin_and_medication",
  component: InsulinAndMedication,
})

const settingsActivityRoute = new Route({
  loader: (() => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({
      to: "/settings/day_stats",
    })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }) as () => any,
  getParentRoute: () => settingsRoute,
  path: "/activity",
  component: Outlet,
})

const settingsDayStatsRoute = new Route({
  getParentRoute: () => settingsRoute,
  path: "/day_stats",
  component: DayStats,
})

const settingsDayStatsFollowRoute = new Route({
  getParentRoute: () => settingsRoute,
  path: "/day_stats/$accountId",
  component: DayStats,
})

const settingsReportsRoute = new Route({
  getParentRoute: () => settingsRoute,
  path: "/reports",
  component: Reports,
})

const settingsReportsFollowRoute = new Route({
  getParentRoute: () => settingsRoute,
  path: "/reports/$accountId",
  component: Reports,
})

const sourcesCreatedRoute = new Route({
  getParentRoute: () => privateRoute,
  path: "/sources/created",
  component: SourcesCreated,
})

const dataSharingRoute = new Route({
  getParentRoute: () => privateRoute,
  path: "/data_sharing",
  component: Outlet,
})

const dataSharingIndexRoute = new Route({
  getParentRoute: () => dataSharingRoute,
  path: "/",
  component: DataSharingIndex,
})

const dataSharingSharingRoute = new Route({
  getParentRoute: () => dataSharingRoute,
  path: "/sharing",
})

const dataSharingSharingIndexRoute = new Route({
  getParentRoute: () => dataSharingSharingRoute,
  path: "/",
  component: Sharing,
})

const dataSharingInviteRoute = new Route({
  getParentRoute: () => dataSharingSharingRoute,
  path: "/invite",
  component: InviteFollower,
})

const dataSharingManageFollowerRoute = new Route({
  getParentRoute: () => dataSharingSharingRoute,
  path: "/$followId/manage_follower",
  component: ManageFollower,
})

const dataSharingManageInvitationRoute = new Route({
  getParentRoute: () => dataSharingSharingRoute,
  path: "/$followInviteId/manage_invitation",
  component: ManageInvitation,
})

const dataSharingFollowingRoute = new Route({
  getParentRoute: () => dataSharingRoute,
  path: "/following",
})

const dataSharingFollowingIndexRoute = new Route({
  getParentRoute: () => dataSharingFollowingRoute,
  path: "/",
  component: Following,
})

const dataSharingFollowerRoute = new Route({
  getParentRoute: () => dataSharingFollowingRoute,
  path: "/$followId/manage_following",
  component: ManageFollowing,
})

const dataSharingAcceptInviteRoute = new Route({
  getParentRoute: () => dataSharingFollowingRoute,
  path: "/accept_invite",
})

const dataSharingAcceptInviteIndexRoute = new Route({
  getParentRoute: () => dataSharingAcceptInviteRoute,
  path: "/",
  component: DataSharingAcceptInvite,
})

const dataSharingAcceptInviteSuccessRoute = new Route({
  getParentRoute: () => dataSharingAcceptInviteRoute,
  path: "/$accountId/success",
  component: DataSharingInviteAccepted,
})

const dataSharingAcceptInvitePhoneCallAlertsRoute = new Route({
  getParentRoute: () => dataSharingAcceptInviteRoute,
  path: "/$accountId/phone_call_alerts",
  component: DataSharingFollowerPhoneCallAlerts,
})

const dataSharingAcceptInviteThemeRoute = new Route({
  getParentRoute: () => dataSharingAcceptInviteRoute,
  path: "/$accountId/theme",
  component: DataSharingFollowerTheme,
})

export const routeTree = rootRoute.addChildren([
  publicRoute.addChildren([
    indexRoute,
    signUpRoute,
    signInRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    policyViewerRoute,
  ]),
  privateRoute.addChildren([
    onboardingUnitsOfMeasureRoute,
    onboardingGetStartedRoute,
    onboardingDataSourceRoute.addChildren([
      onboardingDataSourceIndexRoute,
      onboardingNightscoutRoute,
      onboardingDataConsentRoute,
    ]),
    onboardingRangesRoute,
    onboardingPhoneCallAlertsRoute,
    onboardingThemeRoute,
    onboardingAcceptInviteRoute.addChildren([
      onboardingAcceptInviteIndexRoute,
      onboardingAcceptInviteSuccessRoute,
      onboardingAcceptInvitePhoneCallAlertsRoute,
      onboardingAcceptInviteThemeRoute,
    ]),
    nightstandRoute,
    nightstandFollowRoute,
    homeRoute,
    homeFollowRoute,
    sourcesCreatedRoute,
    accountRoute.addChildren([
      accountIndexRoute,
      accountDataSourceRoute.addChildren([
        accountDataSourceIndexRoute,
        dataConsentRoute,
        nightscoutRoute,
      ]),
      accountDetailsRoute,
    ]),
    helpAndFeedbackRoute.addChildren([
      helpAndFeedbackIndexRoute,
      helpAndFeedbackHelpRoute,
      helpAndFeedbackAboutRoute,
      helpAndFeedbackPrivacyRoute,
    ]),
    settingsRoute.addChildren([
      settingsActivityRoute,
      settingsDayStatsRoute,
      settingsDayStatsFollowRoute,
      settingsThemeRoute,
      settingsThemeFollowRoute,
      settingsDisplayRoute,
      settingsDisplayFollowRoute,
      settingsInsulinAndMedicationRoute,
      settingsRangesRoute,
      settingsRangesFollowRoute,
      settingsIndexRoute,
      settingsIndexFollowRoute,
      settingsReportsRoute,
      settingsReportsFollowRoute,
      alertsRoute.addChildren([
        alertsIndexRoute,
        alertsIndexFollowRoute,
        alertsSMSRoute,
        alertsSMSFollowRoute,
        alertsPhoneCall,
        alertsPhoneCallFollowRoute,
      ]),
    ]),
    dataSharingRoute.addChildren([
      dataSharingIndexRoute,
      dataSharingSharingRoute.addChildren([
        dataSharingSharingIndexRoute,
        dataSharingInviteRoute,
        dataSharingManageFollowerRoute,
        dataSharingManageInvitationRoute,
      ]),
      dataSharingFollowingRoute.addChildren([
        dataSharingFollowingIndexRoute,
        dataSharingFollowerRoute,
        dataSharingAcceptInviteRoute.addChildren([
          dataSharingAcceptInviteIndexRoute,
          dataSharingAcceptInviteSuccessRoute,
          dataSharingAcceptInvitePhoneCallAlertsRoute,
          dataSharingAcceptInviteThemeRoute,
        ]),
      ]),
    ]),
  ]),
  oauthAuthorizeRootRoute.addChildren([
    oauthAuthorizeIndexRoute,
    oauthAuthorizeSignUpRoute,
    oauthAuthorizeSignInRoute,
    oauthAuthorizeForgotPasswordRoute,
  ]),
])

export const router = new Router({
  routeTree,
  defaultPreload: "intent",
})

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
